import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { NavigationStackContextValue, NavigationStackItem } from "../type/type";

const NavigationStackContext = createContext<
  NavigationStackContextValue | undefined
>(undefined);

export const NavigationStackProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const [navigationStack, setNavigationStack] = useState<NavigationStackItem[]>(
    [],
  );
  // useRef to track latest stack for reliable pop operation
  const stackRef = useRef<NavigationStackItem[]>([]);

  // Sync ref with state after render
  useEffect(() => {
    stackRef.current = navigationStack;
  }, [navigationStack]);

  const pushToStack = useCallback((item: NavigationStackItem) => {
    setNavigationStack((prev) => [...prev, item]);
  }, []);

  const popFromStack = useCallback((): NavigationStackItem | undefined => {
    const currentStack = stackRef.current;
    if (currentStack.length === 0) {
      return undefined;
    }
    const poppedItem = currentStack[currentStack.length - 1];
    setNavigationStack(currentStack.slice(0, -1));
    return poppedItem;
  }, []);

  const clearStack = useCallback(() => {
    setNavigationStack([]);
  }, []);

  const setStack = useCallback((stack: NavigationStackItem[]) => {
    setNavigationStack(stack);
  }, []);

  const value = useMemo(
    () => ({
      navigationStack,
      pushToStack,
      popFromStack,
      clearStack,
      setStack,
    }),
    [navigationStack, pushToStack, popFromStack, clearStack, setStack],
  );

  return (
    <NavigationStackContext.Provider value={value}>
      {children}
    </NavigationStackContext.Provider>
  );
};

export const useNavigationStack = (): NavigationStackContextValue => {
  const context = useContext(NavigationStackContext);
  if (context === undefined) {
    throw new Error(
      "useNavigationStack must be used within a NavigationStackProvider",
    );
  }
  return context;
};
